/* R39 — optional ambience: no canvas allocation in a hidden world or data-saving mode. */
(() => {
  'use strict';
  if (window.__vpAtmosphereController) return;
  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(pointer: fine)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const instances = [];
  const cleanup = [];
  let transition = false;
  let suspended = false;
  let disposed = false;
  const listen = (target, type, handler, options) => {
    if (!target?.addEventListener) return;
    target.addEventListener(type, handler, options);
    cleanup.push(() => target.removeEventListener(type, handler, options));
  };
  function dataSaving() {
    return !!(connection && (connection.saveData || /^(slow-2g|2g|3g)$/.test(connection.effectiveType || '')));
  }
  document.querySelectorAll('[data-atmosphere]').forEach(section => {
    const canvas = section.querySelector('[data-particle-canvas]');
    if (!canvas) return;
    const button = section.querySelector('[data-atmosphere-toggle]');
    const host = canvas.parentElement;
    let ctx = null, unsupported = false;
    let visible = false, enabled = true, raf = 0, last = 0, w = 0, h = 0, tick = 0;
    let particles = [], pointer = { x: .5, y: .5 };
    let needsResize = true;
    function reason() {
      if (disposed || suspended) return 'suspended';
      if (unsupported) return 'unsupported';
      if (reduce.matches) return 'reduced-motion';
      if (dataSaving()) return 'data-saving';
      if (!enabled) return 'manual';
      if (document.hidden) return 'background';
      if (transition) return 'transition';
      if (root.dataset.entry === 'gateway') return 'gateway';
      if (root.dataset.world !== section.dataset.atmosphere) return 'other-world';
      if (!visible) return 'offscreen';
      return '';
    }
    function stop(state) {
      if (raf) cancelAnimationFrame(raf);
      raf = 0; last = 0;
      if (ctx) ctx.clearRect(0, 0, w, h);
      canvas.dataset.running = 'false';
      canvas.dataset.atmosphereState = state || 'idle';
    }
    function resize() {
      if (!ctx) return false;
      const rect = host.getBoundingClientRect();
      w = rect.width; h = rect.height;
      if (!w || !h) return false;
      const mobile = innerWidth < 768;
      const dpr = Math.min(devicePixelRatio || 1, mobile ? 1.25 : 1.5);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = Array.from({ length: mobile ? 12 : innerWidth < 1100 ? 24 : 40 }, () => ({
        x: Math.random(), y: Math.random(), r: .6 + Math.random() * 1.8,
        speed: .0005 + Math.random() * .0012, phase: Math.random() * Math.PI * 2
      }));
      needsResize = false;
      return true;
    }
    function frame(time) {
      raf = 0;
      const blocked = reason();
      if (blocked) { stop(blocked); return; }
      if (time - last > (innerWidth < 768 ? 50 : 32)) {
        const dt = Math.min(60, time - (last || time)); last = time; tick += dt * .001;
        ctx.clearRect(0, 0, w, h);
        particles.forEach(p => {
          p.y -= p.speed * dt * .04;
          if (p.y < -.03) p.y = 1.03;
          const drift = Math.sin(tick * .3 + p.phase) * 10 + (pointer.x - .5) * 8;
          ctx.globalAlpha = .22 + (Math.sin(tick + p.phase) + 1) * .22;
          ctx.fillStyle = p.r > 1.6 ? '#ffffff' : '#9670ab';
          ctx.beginPath(); ctx.arc(p.x * w + drift, p.y * h, p.r, 0, Math.PI * 2); ctx.fill();
        });
      }
      raf = requestAnimationFrame(frame);
    }
    function sync() {
      const blocked = reason();
      const policy = reduce.matches || dataSaving() || unsupported;
      if (button) {
        button.disabled = policy;
        button.setAttribute('aria-pressed', String(enabled && !policy));
        button.setAttribute('aria-label', policy ? 'Ambient particles disabled by device settings' : enabled ? 'Pause ambient particles' : 'Play ambient particles');
        button.querySelector('i')?.setAttribute('class', enabled && !policy ? 'fa-solid fa-pause' : 'fa-solid fa-play');
      }
      if (blocked) { stop(blocked); return; }
      if (!ctx) {
        try { ctx = canvas.getContext('2d', { alpha: true }); } catch (_) { ctx = null; }
        if (!ctx) { unsupported = true; sync(); return; }
      }
      if (needsResize && !resize()) { stop('no-size'); return; }
      if (!raf) { last = 0; raf = requestAnimationFrame(frame); }
      canvas.dataset.running = 'true';
      canvas.dataset.atmosphereState = 'running';
    }
    listen(button, 'click', () => { enabled = !enabled; sync(); });
    listen(host, 'pointermove', event => {
      if (!fine.matches || reason()) return;
      const rect = host.getBoundingClientRect();
      if (rect.width) pointer.x = (event.clientX - rect.left) / rect.width;
    }, { passive: true });
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: .05 });
      observer.observe(host);
      cleanup.push(() => observer.disconnect());
    } // Without visibility observation, keep the harmless static background.
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(() => { needsResize = true; sync(); });
      observer.observe(host);
      cleanup.push(() => observer.disconnect());
    } else listen(window, 'resize', () => { needsResize = true; sync(); }, { passive: true });
    instances.push({ sync, stop });
    sync();
  });
  const syncAll = () => { if (!disposed) instances.forEach(instance => instance.sync()); };
  function dispose() {
    if (disposed) return;
    disposed = true;
    instances.forEach(instance => instance.stop('disposed'));
    cleanup.splice(0).forEach(fn => fn());
    delete window.__vpAtmosphereController;
  }
  listen(document, 'visibilitychange', syncAll);
  listen(window, 'vp:worldchange', syncAll);
  listen(window, 'vp:entryready', syncAll);
  listen(window, 'vp:transitionstart', () => { transition = true; syncAll(); });
  listen(window, 'vp:transitionend', () => { transition = false; syncAll(); });
  listen(window, 'pageshow', () => { suspended = false; transition = false; syncAll(); });
  listen(window, 'pagehide', event => {
    if (!event.persisted) { dispose(); return; }
    suspended = true; syncAll();
  });
  listen(reduce, 'change', syncAll);
  listen(connection, 'change', syncAll);
  window.__vpAtmosphereController = { dispose };
})();
